import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const DARK_BROWN = [59, 36, 16];
const GOLD       = [201, 168, 76];
const LIGHT_GOLD = [255, 255, 255]; // white
const BLACK      = [0, 0, 0];
const GREY_LIGHT = [245, 245, 245];
const GREY_MID   = [200, 200, 200];
const BLUE_LINK  = [0, 102, 204];

function buildDoc(billData, companyData = {}) {
  const doc = new jsPDF('l', 'mm', 'a4');
  const pw  = doc.internal.pageSize.getWidth();
  const ph  = doc.internal.pageSize.getHeight();
  const lm  = 6;
  const rm  = pw - lm;

  const companyName    = companyData.Name           || 'COCHIN FRAMES';
  const companyPhone1  = companyData.ContactNumber1 || '0484-4864027';
  const companyPhone2  = companyData.ContactNumber2 || '7356864227';
  const companyPhone3  = companyData.ContactNumber3 || '9037559716';
  const companyEmail   = companyData.Email          || 'cochinframes07@gmail.com';
  const companyWeb     = companyData.Website        || 'www.cochinframes.com';
  const companyAddr1   = companyData.AddressLine1   || 'Edappally -';
  const companyAddr2   = companyData.AddressLine2   || 'Tripunithura Road,';
  const companyCity    = companyData.Location       || 'Padivattom, Kochi-24.';

  // Top bar
  doc.setFillColor(245, 240, 230);
  doc.rect(0, 0, pw, 6, 'F');
  doc.setFillColor(245, 240, 230);
  doc.rect(0, 6, pw, 1.5, 'F');

  // Company name
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_BROWN);
  doc.text(companyName.toUpperCase(), pw / 2, 18, { align: 'center' });

  const nameWidth = doc.getTextWidth(companyName.toUpperCase());
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.6);
  doc.line(pw / 2 - nameWidth / 2 - 2, 20, pw / 2 + nameWidth / 2 + 2, 20);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 70, 30);
  doc.text(
    'Photo Framing  |  Certificate Framing  |  Artwork Framing  |  Photo Restoration  |  Collage Framing',
    pw / 2, 25, { align: 'center' }
  );

  // Bill banner
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.4);
  doc.roundedRect(lm, 28, pw - lm * 2, 9, 2, 2, 'S');
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text('INVOICE', pw / 2, 34.5, { align: 'center' });

  // Customer info box
  const boxY = 40;
  const boxH = 22;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(lm, boxY, pw - lm * 2, boxH, 2, 2, 'S');

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.2);
  doc.line(lm + 140, boxY + 1, lm + 140, boxY + boxH - 1);
  doc.line(lm + 220, boxY + 1, lm + 220, boxY + boxH - 1);

  const infoY = boxY + 6;
  const gap   = 6;

  const lc = lm + 3;
  const lv = lm + 24;
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);

  infoRow(doc, lc, lv, infoY,       'Name',    billData.ClientName || 'WALK-IN');
  infoRow(doc, lc, lv, infoY + gap, 'Contact', billData.ContactNumber || '—');

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text('Remarks', lc, infoY + gap * 2);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);
  const remarksLines = doc.splitTextToSize(billData.Remarks || '', 100);
  doc.text(': ' + (remarksLines[0] || ''), lv, infoY + gap * 2);
  if (remarksLines.length > 1) doc.text(remarksLines.slice(1), lv + 2, infoY + gap * 2 + 4);

  const mc = lm + 143;
  const mv = lm + 160;
  infoRow(doc, mc, mv, infoY,            'Total',   fmt(billData.TotalAmount));
  infoRow(doc, mc, mv, infoY + gap,      'Advance', fmt(billData.AdvanceAmount));
  infoRow(doc, mc, mv, infoY + gap * 2,  'Balance', fmt(billData.BalanceAmount));

  const rc = lm + 223;
  const rv = lm + 244;
  infoRow(doc, rc, rv, infoY,            'Bill No',   buildBillNo(billData, companyData.BillPrefix));
  infoRow(doc, rc, rv, infoY + gap,      'Bill Date', fmtDate(billData.BillDate));
  infoRow(doc, rc, rv, infoY + gap * 2,  'Fin Year',  billData.FinYear || '—');

  // Details table
  const tableStartY = boxY + boxH + 4;
  const details = billData.BillDetails || [];

  const rows = details.map((d, i) => [
    i + 1,
    sizeLabel(d),
    getName(d, 'Frame'),
    getName(d, 'Glass'),
    getName(d, 'Lamination'),
    getName(d, 'Mount'),
    d.MountWidth ? String(d.MountWidth) : 'NIL',
    getName(d, 'Board'),
    d.HookCount > 0 ? String(d.HookCount) : 'NIL',
    getName(d, 'Stand'),
    getPrintName(d),
    parseFloat(d.Rate || 0).toFixed(2),
    String(d.Quantity || 1),
    parseFloat(d.Amount || 0).toFixed(2),
  ]);

  while (rows.length < 8) rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '']);

  autoTable(doc, {
    startY: tableStartY,
    head: [['SN', 'WXH(INCH)', 'Frame', 'Glass', 'Lamn', 'Mount', 'MNT-WIDTH', 'Board', 'Hook', 'Stand', 'Print', 'Rate', 'Qty', 'Amount']],
    body: rows,
    theme: 'grid',
    tableWidth: pw - lm * 2,
    styles: {
      fontSize: 7.5,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      lineColor: GOLD,
      lineWidth: 0.15,
      textColor: [30, 30, 30],
      overflow: 'linebreak',
      font: 'helvetica',
    },
    headStyles: {
      fillColor: false,
      textColor: BLACK,
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 7.5,
      lineWidth: 0.2,
      lineColor: GOLD,
      minCellHeight: 7,
    },
    alternateRowStyles: { fillColor: LIGHT_GOLD },
    columnStyles: {
      0:  { halign: 'center', cellWidth: 9  },   // SN
      1:  { halign: 'center', cellWidth: 21 },   // WXH
      2:  { cellWidth: 47 },                      // Frames
      3:  { cellWidth: 15 },                      // Glass
      4:  { cellWidth: 15 },                      // Lamn
      5:  { cellWidth: 19 },                      // Mount
      6:  { halign: 'center', cellWidth: 23 },   // MNT-WIDTH
      7:  { cellWidth: 22 },                      // Board
      8:  { halign: 'center', cellWidth: 13 },   // Hook
      9:  { cellWidth: 20 },                      // Stand
      10: { cellWidth: 27 },                      // Print
      11: { halign: 'right',  cellWidth: 19 },   // Rate
      12: { halign: 'center', cellWidth: 14 },   // Qty
      13: { halign: 'right',  cellWidth: 18 },   // Amount
    },
    margin: { left: lm, right: lm },
  });

  // Totals
  const afterTable = (doc.lastAutoTable?.finalY ?? tableStartY + 60) + 3;
  const totBoxW = 70;
  const totBoxX = rm - totBoxW;

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(totBoxX, afterTable, totBoxW, 32, 2, 2, 'S');

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);

  const totRows = [
    ['Amount',         fmt(billData.Amount)],
    ['Packing Charge', fmt(billData.PackingCharge)],
    ['Other Amount',   fmt(billData.OtherAmount)],
    ['Round Off',      fmt(billData.Roundoff)],
  ];

  let ty = afterTable + 6;
  totRows.forEach(([label, value]) => {
    doc.text(label, rm - 22, ty, { align: 'right' });
    doc.text(value, rm - 2, ty, { align: 'right' });
    ty += 5;
  });

  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(totBoxX, ty - 4, totBoxW, 7, 1, 1, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text('Total', rm - 22, ty + 0.5, { align: 'right' });
  doc.text(fmt(billData.TotalAmount), rm - 2, ty + 0.5, { align: 'right' });

  // Payment summary (left of totals)
  const payY = afterTable + 4;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_BROWN);
  doc.text('Payment Summary', lm, payY);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);
  const payRows = [
    ['Advance', fmt(billData.AdvanceAmount)],
    ['Cash',    fmt(billData.PaidAmount)],
    ['Card',    fmt(billData.CardAmount)],
    ['UPI',     fmt(billData.UpiAmount)],
    ['Balance', fmt(billData.BalanceAmount)],
  ];
  let py = payY + 5;
  payRows.forEach(([label, value]) => {
    doc.text(label + ' :', lm + 2, py);
    doc.text(value, lm + 30, py, { align: 'right' });
    py += 4.5;
  });

  // Signature
  ty += 12;
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);

  // Footer
  const fy = ph - 14;
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.8);
  doc.line(lm, fy - 4, rm, fy - 4);

  // For Company + Authorised Signatory
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK_BROWN);
  doc.setFontSize(10);
  doc.text(`For ${companyName}`, rm, fy - 17, { align: 'right' });
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);
  doc.text('Authorised  Signatory', rm, fy - 5, { align: 'right' });

  // Bottom bar
  doc.setFillColor(245, 240, 230);
  doc.rect(0, ph - 8, pw, 8, 'F');

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 40, 10);

  const colW = (rm - lm) / 4;
  // Address — 2 lines
  doc.text(`${companyAddr1} ${companyAddr2}`, lm,              fy);
  doc.text(companyCity,                       lm,              fy + 5);
  // Phones — 2 lines
  doc.text(`Tel : ${companyPhone1}  Mob : ${companyPhone2}`, lm + colW * 2, fy);
  doc.text(companyPhone3,                                     lm + colW * 2, fy + 5);
  doc.setTextColor(...BLUE_LINK);
  doc.text(companyEmail, lm + colW * 3, fy);
  doc.text(companyWeb,   lm + colW * 3, fy + 5);

  // Text in bottom bar
  doc.setTextColor(...BLACK);
  doc.setFontSize(7);
  doc.text(`© ${companyName} — Quality Framing Since 2000`, pw / 2, ph - 3.5, { align: 'center' });

  return doc;
}

export const printBillPDF = (billData, companyData = {}) => {
  const doc = buildDoc(billData, companyData);
  const blobUrl = doc.output('bloburl');
  const win = window.open(blobUrl, '_blank');
  if (win) win.addEventListener('load', () => { win.focus(); win.print(); });
};

export const downloadBillPDF = (billData, companyData = {}) => {
  const doc = buildDoc(billData, companyData);
  doc.save(`Bill_${buildBillNo(billData, companyData.BillPrefix)}_${(billData.ClientName || 'Bill').replace(/\s+/g, '_')}.pdf`);
};

export const shareBillWhatsApp = async (billData, companyData = {}) => {
  const doc   = buildDoc(billData, companyData);
  const name  = billData.ClientName || 'Customer';
  const billNo = buildBillNo(billData, companyData.BillPrefix);
  const total = fmt(billData.TotalAmount);
  const bal   = fmt(billData.BalanceAmount);
  const date  = fmtDate(billData.BillDate);
  const cName  = companyData.Name || 'Cochin Frames';
  const cPhone = companyData.ContactNumber2 || companyData.ContactNumber1 || '7356864227';
  const cWeb   = companyData.Website || 'www.cochinframes.com';

  const message =
    `🖼️ *${cName.toUpperCase()} — Bill / Invoice*\n\n` +
    `📋 Bill No  : *${billNo}*\n` +
    `👤 Name     : *${name}*\n` +
    `📅 Date     : ${date}\n` +
    `💰 Total    : ₹${total}\n` +
    `💳 Balance  : ₹${bal}\n\n` +
    `_Thank you for choosing ${cName}!_\n` +
    `📞 ${cPhone} | 🌐 ${cWeb}`;

  if (navigator.canShare) {
    try {
      const pdfBlob = doc.output('blob');
      const file = new File([pdfBlob], `Bill_${billNo}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Bill ${billNo}`, text: message });
        return;
      }
    } catch (_) {}
  }

  const phone = (billData.ContactNumber || '').replace(/\D/g, '');
  const url = phone
    ? `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank');
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function infoRow(doc, labelX, valueX, y, label, value) {
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text(label, labelX, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(': ' + value, valueX, y);
}

function fmt(val) { return parseFloat(val || 0).toFixed(2); }

function fmtDate(dateStr) {
  if (!dateStr) return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${d.toLocaleString('en-GB', { month: 'short' })}/${d.getFullYear()}`;
}

function buildBillNo(bill, billPrefix) {
  return `${billPrefix || 'BILL'}${bill.BillNumber || 0}`;
}

function sizeLabel(detail) {
  const w = detail.Width || '';
  const h = detail.Height || '';
  if (w && h) return `${w}X${h}`;
  return detail.Size?.Name || '';
}

function getName(detail, objKey) {
  if (detail[objKey]?.Name) return detail[objKey].Name;
  return 'NIL';
}

function getPrintName(detail) {
  if (detail.Print?.PrintType) return detail.Print.PrintType;
  if (detail.Print?.Name) return detail.Print.Name;
  return 'NIL';
}
